import React from 'react';
import axios from 'axios';
import PropTypes from 'prop-types';

import { SCOPES } from '@techchange/components';

import usePointSettings from '../hooks/usePointSettings';
import usePointTotal from '../hooks/usePointTotal';

import UserNavDropdownMenu from './UserNavDropdownMenu';

const axiosClient = axios.create({
	withCredentials: true,
});

const propTypes = {

	/**
	 * The current user.
	 */
	currentUser: PropTypes.shape({

		/**
		 * The user's ID.
		 */
		id: PropTypes.string.isRequired,

		/**
		 * The user's display name.
		 */
		display_name: PropTypes.string.isRequired,

		/**
		 * The profile pictures for the user.
		 */
		avatars: PropTypes.arrayOf(PropTypes.shape({

			/**
			 * True, if the image is active.
			 */
			active: PropTypes.bool,

			/**
			 * The filstack image url.
			 */
			image: PropTypes.string,

			/**
			 * The filestack security policy.
			 */
			policy: PropTypes.string,

			/**
			 * The filestack security signature.
			 */
			signature: PropTypes.string,

		})),

	}).isRequired,

	/**
	 * The organization for the current user.
	 */
	myOrganization: PropTypes.shape({

		/**
		 * The organization ID.
		 */
		id: PropTypes.string.isRequired,

		/**
		 * The organization title.
		 */
		title: PropTypes.string.isRequired,

	}).isRequired,

	/**
	 * True, if the current user has admin privileges for the organization.
	 */
	isAdmin: PropTypes.bool,

	/**
	 * True, if the current user has admin privileges for the active course session (if any).
	 */
	isSessionAdmin: PropTypes.bool,

	/**
	 * The active course session (if any).
	 */
	session: PropTypes.shape({

		/**
		 * The session ID.
		 */
		id: PropTypes.string.isRequired,

		/**
		 * The session slug.
		 */
		slug: PropTypes.string.isRequired,

		/**
		 * The active course (if any).
		 */
		course: PropTypes.shape({

			/**
			 * The course ID.
			 */
			id: PropTypes.string.isRequired,

			/**
			 * The course title.
			 */
			title: PropTypes.string.isRequired,

		}),

	}),

	/**
	 * The course slug (i.e. base URL + session slug) to use as prefix for session specific links.
	 */
	courseSlug: PropTypes.string,

	/**
	 * The point label for the organization.
	 */
	pointLabel: PropTypes.shape({

		/**
		 * The point label id.
		 */
		id: PropTypes.string,

		/**
		 * The singular label.
		 */
		singular_label: PropTypes.string,

		/**
		 * The plural label.
		 */
		plural_label: PropTypes.string,

	}),

	/**
	 * NOTE: Temporary flag to hide fields related to automatic
	 * translation.
	 */
	hideTranslationFields: PropTypes.bool,

	/**
	 * Callback to handle updating the user's language preference.
	 */
	onSelectLanguage: PropTypes.func,

};

const UserNavDropdown = (props) => {

	const {
		currentUser,
		myOrganization,
		isAdmin,
		isSessionAdmin,
		session,
		courseSlug,
		pointLabel,
		hideTranslationFields,
		onSelectLanguage,
	} = props;

	// Check if points are enabled for the session
	const { pointsEnabled } = usePointSettings({
		scope: SCOPES.SESSION,
		scope_id: session && session.id,
	 });

	// Keep track of the user's point total for the session (on dropdown menu opened for first time)
	const [sessionPointTotal, fetchSessionPointTotal] = usePointTotal({
		user_id: currentUser.id,
		scope: SCOPES.SESSION,
		scope_id: session && session.id,
		organization_id: myOrganization.id,
	});

	// Handles loading the required data for the menu
	const handleLoadMenuData = () => {

		if (!session || !pointsEnabled) { return; }

		fetchSessionPointTotal();

	};

	// Handles logging out the current user
	const handleLogout = () => {

		axiosClient.post('/api/v2/users/logout', {}).then(() => {

			window.location.href = '/';

		}).catch((error) => {
			console.error(error);
		});

	};

	return (
		<UserNavDropdownMenu
			currentUser={currentUser}
			myOrganization={myOrganization}
			isAdmin={isAdmin}
			isSessionAdmin={isSessionAdmin}
			session={session}
			courseSlug={courseSlug}
			pointLabel={pointLabel}
			pointsEnabled={pointsEnabled}
			sessionPointTotal={sessionPointTotal}
			hideTranslationFields={hideTranslationFields}
			onSelectLanguage={onSelectLanguage}
			onLoadMenuData={handleLoadMenuData}
			onLogout={handleLogout} />
	);

};

UserNavDropdown.propTypes = propTypes;

export default UserNavDropdown;
